import { useEffect, useMemo, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Layers, CheckCircle2, ImageIcon, XCircle, Wand2, Edit2, Move, BookOpen, Sparkles, ZoomIn, AlertTriangle } from "lucide-react";
import { api, formatCOP } from "@/lib/api";
import { toast } from "sonner";
import ImageLightbox from "@/components/ImageLightbox";

/**
 * Paso 3 del wizard de importación de contenedor: "Agrupar por familia".
 *
 * Cada bloque es una familia (agrupación por familia_key_sugerida o override).
 * Por cada familia el director puede:
 *   - Fusionar con familia existente (buscador autocomplete o sugerencia del sistema).
 *   - Renombrar el estilo (aplica a todas las variantes).
 *   - Elegir fuente de foto (Excel / heredar / sin_foto).
 *   - Ajustar precios (precio_sug y precio_min a nivel de familia).
 *
 * NUEVO:
 *   - Miniatura por variante (extraída del Excel).
 *   - Selección múltiple + mover ítems entre familias.
 *   - Drag & drop de ítems entre familias.
 *   - Badge de confianza y "sugerido por diccionario".
 *   - Auto-persistencia del diccionario al confirmar fusiones (backend).
 */
export default function AgruparFamiliasStep({ rows, setRows, onContinuar, onVolver, loading = false }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [busca, setBusca] = useState("");
  const [soloSinDecidir, setSoloSinDecidir] = useState(false);
  const [expandida, setExpandida] = useState({});
  // Feb/2026: cuando hay búsqueda activa, dentro de cada familia visible solo
  // mostramos las filas que matchean. Este state permite al usuario ver TODAS
  // las variantes de una familia específica aunque no todas matcheen.
  const [verTodasEnFam, setVerTodasEnFam] = useState({});
  const [buscadorAbierto, setBuscadorAbierto] = useState({}); // { familiaKey: string }
  const [sugerencias, setSugerencias] = useState({}); // { familiaKey: familias[] }
  // Selección múltiple: set de índices absolutos de rows que están tickeados
  const [seleccion, setSeleccion] = useState(new Set());
  const [moverAbiertoPara, setMoverAbiertoPara] = useState(null); // familiaKey origen o "seleccion"
  const [moverBusqueda, setMoverBusqueda] = useState(""); // filtro dentro del modal "Mover a..."
  const [nuevoNombreFamilia, setNuevoNombreFamilia] = useState("");
  // Lightbox: guardamos la familiaKey y el idx del item dentro de esa familia
  // para poder navegar con flechas ← → entre items del mismo grupo.
  const [lightbox, setLightbox] = useState(null); // { familiaKey, index }
  const draggingRef = useRef(null); // { idx, sku }
  // Feb/2026 fix: cuando fusionamos con una familia existente el grupo cambia
  // de key y visualmente parece que "desaparece" porque su card se desmonta y
  // se monta en otra posición. Marcamos la key destino para hacer scroll suave
  // y resaltarla un momento en el próximo render.
  const [scrollTargetKey, setScrollTargetKey] = useState(null);
  const [highlightKey, setHighlightKey] = useState(null);
  const familiaRefs = useRef({}); // { key: HTMLElement }

  // Agrupar rows por familia_key_sugerida (o override si ya existe)
  const familias = useMemo(() => {
    const map = new Map();
    rows.forEach((r, idx) => {
      const key = (r.familia_key_final || r.familia_key_sugerida || `_solo_${r.sku}`).toString();
      if (!map.has(key)) {
        map.set(key, { key, rows: [], indices: [] });
      }
      map.get(key).rows.push({ ...r, __idx: idx });
      map.get(key).indices.push(idx);
    });
    return Array.from(map.values());
  }, [rows]);

  // Auto-match: preseleccionar fusiones sugeridas (por match exacto o diccionario, no fuzzy)
  const [autoMatchDone, setAutoMatchDone] = useState(false);
  useEffect(() => {
    if (autoMatchDone || !rows.length) return;
    let sugeridas = 0;
    let porDiccionario = 0;
    setRows((rs) => rs.map((r) => {
      if (r.familia_existente && !r.familia_key_final && !r.existe) {
        const fuente = r.familia_existente.fuente || "exact";
        const conf = r.familia_existente.confianza || 0;
        // Solo auto-aplicamos matches con alta confianza (exact/diccionario/fuzzy>=95)
        if (fuente === "exact" || fuente === "diccionario" || conf >= 95) {
          sugeridas += 1;
          if (fuente === "diccionario") porDiccionario += 1;
          return {
            ...r,
            familia_key_final: r.familia_existente.familia_key,
            nombre_familia_override: r.familia_existente.nombre_representativo || r.nombre_familia_override,
            foto_fuente: r.foto_fuente || "heredar",
            foto_heredar_de_producto_id: r.familia_existente.foto_producto_id || null,
            // Feb/2026: guardamos los precios repr para que el paso 3 muestre "Usar estos"
            // también en los matches automáticos, no solo en la fusión manual.
            familia_precios_ref: {
              precio_sugerido: Number(r.familia_existente.precio_sugerido_representativo) || null,
              precio_minimo: Number(r.familia_existente.precio_minimo_representativo) || null,
              costo: Number(r.familia_existente.costo_representativo) || null,
            },
            precio_sugerido_excel_original: r.precio_sugerido,
            precio_minimo_excel_original: r.precio_minimo,
            familia_precios_modo: null,
          };
        }
      }
      return r;
    }));
    if (sugeridas > 0) {
      let msg = `Auto-match: ${sugeridas} productos preseleccionados para fusionar.`;
      if (porDiccionario > 0) msg += ` (${porDiccionario} desde diccionario)`;
      toast.success(msg);
    }
    setAutoMatchDone(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  // Filtro de búsqueda + "solo sin decidir"
  // Feb/2026: soporta múltiples términos separados por espacio, coma o Enter.
  // Ej: "SKU-001, SKU-002, SKU-003" — filtra si CUALQUIER row de la familia
  // matchea con AL MENOS UNO de los términos.
  const filtroInfo = useMemo(() => {
    const raw = busca.trim();
    if (!raw) return { terminos: [], multi: false };
    // Separadores: comas, espacios múltiples, saltos de línea, tabs, punto y coma
    const terminos = raw
      .split(/[,;\n\t\s]+/)
      .map((t) => t.trim().toLowerCase())
      .filter((t) => t.length > 0);
    return { terminos, multi: terminos.length > 1 };
  }, [busca]);

  // Función helper: ¿matches este row con TODOS los términos del filtro?
  // Feb/2026: buscamos en TODOS los campos relevantes:
  //   - sku (único por variante, ej "LST0023RS")
  //   - familia_key_sugerida (ej "EF26-172" del CLIENT'S REF del proveedor)
  //   - familia_ref_excel (mismo valor que arriba, backup)
  //   - familia_key_final (si ya fusionaste con una familia existente)
  //   - nombre_familia_override (nombre editado por el usuario en el wizard)
  //   - nombre (descripción del proveedor)
  //   - color, size
  const rowMatchesSearch = (r, terminos) => {
    if (!terminos.length) return true;
    const campos = [
      (r.sku || ""),
      (r.familia_key_sugerida || ""),
      (r.familia_ref_excel || ""),
      (r.familia_key_final || ""),
      (r.nombre_familia_override || ""),
      (r.nombre || ""),
      (r.color || ""),
      (r.size || ""),
    ].map((s) => String(s).toLowerCase());
    return terminos.some((t) => campos.some((c) => c.includes(t)));
  };

  // Índices de filas que matchean el filtro (para "seleccionar coincidencias")
  const indicesFiltrados = useMemo(() => {
    if (!filtroInfo.terminos.length) return [];
    const idxs = [];
    rows.forEach((r, i) => {
      if (rowMatchesSearch(r, filtroInfo.terminos)) idxs.push(i);
    });
    return idxs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filtroInfo]);

  // Set para lookup O(1) al pintar highlight en cada row
  const indicesFiltradosSet = useMemo(() => new Set(indicesFiltrados), [indicesFiltrados]);

  const familiasFiltradas = useMemo(() => {
    return familias.filter((fam) => {
      if (soloSinDecidir) {
        const decidida = fam.rows.some((r) => r.familia_key_final || r.foto_fuente);
        if (decidida) return false;
      }
      if (!filtroInfo.terminos.length) return true;
      // Familia se muestra si su key matchea CUALQUIER término, o si alguna
      // row matchea (sku/nombre/color/familia_ref).
      const famKey = fam.key.toLowerCase();
      if (filtroInfo.terminos.some((t) => famKey.includes(t))) return true;
      return fam.rows.some((r) => rowMatchesSearch(r, filtroInfo.terminos));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familias, filtroInfo, soloSinDecidir]);

  // Contador de familias decididas
  const decididas = familias.filter((f) => f.rows.every((r) => r.familia_key_final || r.foto_fuente || r.existe)).length;

  // Feb/2026: auto-heal — si hay filas con fusión activa pero sin precios repr
  // (venían de un preview antiguo), los recuperamos vía /familias/buscar.
  const preciosSolicitadosRef = useRef(new Set());
  useEffect(() => {
    const faltantes = new Set();
    for (const r of rows) {
      const fk = r.familia_key_final;
      if (!fk) continue;
      const ref = r.familia_precios_ref;
      const necesita = !ref || (!ref.precio_sugerido && !ref.precio_minimo && !ref.costo);
      if (necesita && !preciosSolicitadosRef.current.has(fk)) {
        faltantes.add(fk);
      }
    }
    if (faltantes.size === 0) return;
    (async () => {
      for (const fk of faltantes) {
        preciosSolicitadosRef.current.add(fk);
        try {
          const { data } = await api.get(`/productos/familias/buscar?q=${encodeURIComponent(fk)}`);
          const hit = (data || []).find((f) => (f.familia_key || "").toUpperCase() === fk.toUpperCase()) || data?.[0];
          if (!hit) continue;
          const ref = {
            precio_sugerido: Number(hit.precio_sugerido_representativo) || null,
            precio_minimo: Number(hit.precio_minimo_representativo) || null,
            costo: Number(hit.costo_representativo) || null,
          };
          setRows((rs) => rs.map((r) => (r.familia_key_final === fk
            ? { ...r, familia_precios_ref: ref }
            : r
          )));
        } catch (e) {
          // silencioso — el usuario aún puede seguir sin el sidebar de precios
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // Feb/2026 fix: cuando se marca scrollTargetKey (tras fusión o mover),
  // hacemos scroll suave al card y lo resaltamos 1.6s para que el usuario
  // no piense que "todo desapareció". Además, si el filtro top oculta el
  // grupo destino, mostramos un toast que ofrece limpiar filtros.
  useEffect(() => {
    if (!scrollTargetKey) return;
    // Esperar un tick para que React monte el nuevo card con esa key.
    const timer = setTimeout(() => {
      const el = familiaRefs.current[scrollTargetKey];
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightKey(scrollTargetKey);
        setTimeout(() => setHighlightKey(null), 1600);
      } else {
        // El grupo destino no está visible en el DOM: probablemente el filtro
        // top o el toggle "Solo sin decidir" lo están escondiendo.
        const busqActiva = busca.trim().length > 0;
        if (busqActiva || soloSinDecidir) {
          toast.info("El grupo destino quedó fuera del filtro actual. Limpia el buscador para verlo.", {
            action: {
              label: "Limpiar filtros",
              onClick: () => { setBusca(""); setSoloSinDecidir(false); },
            },
            duration: 6000,
          });
        }
      }
      setScrollTargetKey(null);
    }, 60);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollTargetKey]);

  // Fusionar duplicados por normalización
  const familiasDuplicadas = useMemo(() => {
    const groups = {};
    familias.forEach((f) => {
      const norm = f.key.toUpperCase().replace(/\s+/g, " ").trim();
      groups[norm] = groups[norm] || [];
      groups[norm].push(f);
    });
    return Object.values(groups).filter((g) => g.length > 1);
  }, [familias]);

  const fusionarDuplicados = () => {
    let count = 0;
    familiasDuplicadas.forEach((grupo) => {
      const canonica = grupo[0].key;
      grupo.slice(1).forEach((f) => {
        f.indices.forEach((idx) => {
          setRows((rs) => rs.map((r, i) => i === idx ? { ...r, familia_key_final: canonica } : r));
          count += 1;
        });
      });
    });
    if (count > 0) toast.success(`${count} productos movidos a familias canónicas`);
    else toast.info("No se encontraron duplicados por normalización");
  };

  const buscarFamilia = async (famKey, texto) => {
    setBuscadorAbierto({ ...buscadorAbierto, [famKey]: texto });
    if (!texto || texto.length < 1) {
      setSugerencias({ ...sugerencias, [famKey]: [] });
      return;
    }
    // Debounce: cancela búsquedas anteriores para no lanzar 1 request por keystroke.
    // Guardamos el timer en una ref por familia.
    if (!buscarFamilia._timers) buscarFamilia._timers = {};
    if (buscarFamilia._timers[famKey]) {
      clearTimeout(buscarFamilia._timers[famKey]);
    }
    buscarFamilia._timers[famKey] = setTimeout(async () => {
      try {
        const { data } = await api.get("/productos/familias/buscar", { params: { q: texto, limit: 15 } });
        setSugerencias((cur) => ({ ...cur, [famKey]: data }));
      } catch {
        setSugerencias((cur) => ({ ...cur, [famKey]: [] }));
      }
    }, 180);
  };

  const aplicarFusion = (indices, familiaMatch) => {
    // Feb/2026: guardamos los precios "representativos" de la familia existente
    // en cada fila para que en el panel de precios el director pueda decidir
    // si mantener los precios actuales de la familia o poner los del Excel.
    // También guardamos los precios originales del Excel (una vez) para poder
    // toggle de vuelta si el usuario cambia de opinión.
    const preciosRef = {
      precio_sugerido: Number(familiaMatch.precio_sugerido_representativo) || null,
      precio_minimo: Number(familiaMatch.precio_minimo_representativo) || null,
      costo: Number(familiaMatch.costo_representativo) || null,
    };
    setRows((rs) => rs.map((r, i) => indices.includes(i)
      ? {
          ...r,
          familia_key_final: familiaMatch.familia_key,
          nombre_familia_override: familiaMatch.nombre_representativo,
          foto_fuente: r.foto_fuente || "heredar",
          foto_heredar_de_producto_id: familiaMatch.foto_producto_id || null,
          familia_precios_ref: preciosRef,
          // Guarda los precios originales del Excel solo la primera vez.
          precio_sugerido_excel_original: r.precio_sugerido_excel_original ?? r.precio_sugerido,
          precio_minimo_excel_original: r.precio_minimo_excel_original ?? r.precio_minimo,
          // Modo por defecto al fusionar: sin elección explícita todavía.
          familia_precios_modo: r.familia_precios_modo || null,
        }
      : r
    ));
    // Feb/2026 fix: marca el grupo destino para scroll+highlight en próximo render.
    setScrollTargetKey(familiaMatch.familia_key);
    toast.success(`Fusionado con "${familiaMatch.nombre_representativo}"`);
  };

  const limpiarFusion = (indices) => {
    setRows((rs) => rs.map((r, i) => indices.includes(i)
      ? {
          ...r,
          familia_key_final: null,
          nombre_familia_override: null,
          foto_fuente: null,
          foto_heredar_de_producto_id: null,
          familia_precios_ref: null,
          familia_precios_modo: null,
        }
      : r
    ));
  };

  // Aplica en batch los precios de la familia existente a todas las filas del grupo.
  // Desactivamos el flag `aplicar_cambio_precio` para que el paso de confirmación
  // NO sobrescriba los precios en la BD.
  const usarPreciosFamiliaExistente = (indices, preciosRef) => {
    if (!preciosRef) return;
    setRows((rs) => rs.map((r, i) => indices.includes(i)
      ? {
          ...r,
          precio_sugerido: preciosRef.precio_sugerido ?? r.precio_sugerido,
          precio_minimo: preciosRef.precio_minimo ?? r.precio_minimo,
          aplicar_cambio_precio: false,
          notificar_vendedores: false,
          familia_precios_modo: "existente",
        }
      : r
    ));
    toast.success("Precios ACTUALES de la familia aplicados · el Excel no cambiará los precios");
  };

  // Feb/2026: aplica los precios NUEVOS del Excel a todas las variantes del grupo
  // Y activa el flag para que también se propague a la familia existente en la BD.
  const usarPreciosNuevos = (indices) => {
    setRows((rs) => rs.map((r, i) => indices.includes(i)
      ? {
          ...r,
          precio_sugerido: r.precio_sugerido_excel_original ?? r.precio_sugerido,
          precio_minimo: r.precio_minimo_excel_original ?? r.precio_minimo,
          aplicar_cambio_precio: true,
          familia_precios_modo: "excel",
        }
      : r
    ));
    toast.success("Precios NUEVOS del Excel aplicados a TODA la familia (incluye variantes existentes)");
  };

  const renombrar = (indices, nuevoNombre) => {
    setRows((rs) => rs.map((r, i) => indices.includes(i) ? { ...r, nombre_familia_override: nuevoNombre } : r));
  };

  const cambiarPrecios = (indices, campo, valor) => {
    setRows((rs) => rs.map((r, i) => indices.includes(i) ? { ...r, [campo]: Number(valor) || 0 } : r));
  };

  // Feb/2026: toggle "código de proveedor" a nivel de familia.
  // Cuando existe conflicto (existente ya tenía código Y el Excel trae otro),
  // el usuario decide si el nuevo código se vuelve el "vitrina" o mantiene el
  // actual. Sin importar la decisión, el LOTE siempre guarda su propio código.
  const aplicarCambioCodigoFamilia = (indices, aplicar) => {
    setRows((rs) => rs.map((r, i) => indices.includes(i)
      ? { ...r, aplicar_cambio_codigo: !!aplicar }
      : r
    ));
  };

  const setFotoFuente = (indices, fuente) => {
    setRows((rs) => rs.map((r, i) => indices.includes(i) ? { ...r, foto_fuente: fuente } : r));
  };

  // -------- Selección múltiple y mover ítems --------
  const toggleSeleccion = (idx) => {
    setSeleccion((s) => {
      const ns = new Set(s);
      if (ns.has(idx)) ns.delete(idx); else ns.add(idx);
      return ns;
    });
  };
  const toggleSeleccionFamilia = (fam, allSelected) => {
    setSeleccion((s) => {
      const ns = new Set(s);
      fam.indices.forEach((i) => { if (allSelected) ns.delete(i); else ns.add(i); });
      return ns;
    });
  };
  const limpiarSeleccion = () => setSeleccion(new Set());

  // Mueve un conjunto de índices a otra familia (nueva o existente en el wizard)
  const moverItems = (indices, familiaKeyDestino, nombreDestino = null) => {
    if (!indices.length) return;
    setRows((rs) => rs.map((r, i) => indices.includes(i)
      ? {
          ...r,
          familia_key_sugerida: familiaKeyDestino,
          familia_key_final: null, // resetear override; el usuario decide de nuevo en el destino
          nombre_familia_override: nombreDestino || r.nombre_familia_override,
          // Cuando movemos a otra familia, resetear fusiones para evitar mezclas confusas.
          foto_fuente: null,
          foto_heredar_de_producto_id: null,
        }
      : r
    ));
    limpiarSeleccion();
    setMoverAbiertoPara(null);
    setNuevoNombreFamilia("");
    // Feb/2026 fix: marca la familia destino para scroll+highlight.
    setScrollTargetKey(familiaKeyDestino);
    toast.success(`${indices.length} ítem(s) movidos a "${familiaKeyDestino}"`);
  };

  // Drag & drop
  const onDragStartItem = (idx, sku) => (e) => {
    draggingRef.current = { idx, sku };
    e.dataTransfer.setData("text/plain", String(idx));
    e.dataTransfer.effectAllowed = "move";
  };
  const onDropFamilia = (famDestKey) => (e) => {
    e.preventDefault();
    const dragged = draggingRef.current;
    draggingRef.current = null;
    if (!dragged) return;
    // Si hay selección múltiple y el ítem arrastrado forma parte, movemos todos.
    if (seleccion.size > 1 && seleccion.has(dragged.idx)) {
      moverItems(Array.from(seleccion), famDestKey);
    } else {
      moverItems([dragged.idx], famDestKey);
    }
  };
  const onDragOverFamilia = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  return (
    <div className="space-y-4 mt-4 overflow-y-auto flex-1 flex flex-col">
      {/* Header con buscador + acciones */}
      <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
            <Input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por SKU, nombre o color. Puedes pegar varios separados por coma, espacio o Enter"
              className="pl-9 h-10"
              data-testid="paso3-buscar"
            />
          </div>
          <label className="text-xs flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={soloSinDecidir} onChange={(e) => setSoloSinDecidir(e.target.checked)} className="w-4 h-4" />
            Solo sin decidir
          </label>
          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <b>{decididas}</b> de <b>{familias.length}</b> familias decididas
          </div>
          {familiasDuplicadas.length > 0 && (
            <Button size="sm" variant="outline" onClick={fusionarDuplicados} className="border-amber-400 text-amber-800 hover:bg-amber-100" data-testid="fusionar-dup-btn">
              <Wand2 className="w-3.5 h-3.5 mr-1" /> Fusionar {familiasDuplicadas.length} duplicados
            </Button>
          )}
        </div>
        {/* Feb/2026: si el usuario buscó múltiples términos o hay matches,
            mostramos un panel con el conteo y un botón para seleccionarlos todos. */}
        {filtroInfo.terminos.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap text-xs bg-primary/10 rounded-md px-3 py-2 border border-primary/20">
            <Search className="w-3.5 h-3.5 text-primary" />
            {filtroInfo.multi ? (
              <span>Buscando <b>{filtroInfo.terminos.length}</b> términos: {filtroInfo.terminos.slice(0, 5).map((t) => (
                <span key={t} className="inline-block mx-0.5 px-1.5 py-0.5 bg-primary/20 rounded font-mono text-[10px]">{t}</span>
              ))}
              {filtroInfo.terminos.length > 5 && <span className="text-muted-foreground"> +{filtroInfo.terminos.length - 5} más</span>}
              </span>
            ) : (
              <span>Buscando: <b className="font-mono">{filtroInfo.terminos[0]}</b></span>
            )}
            <span className="ml-auto text-muted-foreground">
              <b className="text-foreground">{indicesFiltrados.length}</b> ítem{indicesFiltrados.length !== 1 ? "s" : ""} en <b className="text-foreground">{familiasFiltradas.length}</b> familia{familiasFiltradas.length !== 1 ? "s" : ""}
            </span>
            {indicesFiltrados.length > 0 && (
              <Button
                size="sm"
                variant="default"
                onClick={() => setSeleccion(new Set(indicesFiltrados))}
                className="h-7 text-[11px]"
                data-testid="paso3-seleccionar-coincidencias"
              >
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Seleccionar los {indicesFiltrados.length} ítems
              </Button>
            )}
          </div>
        )}
        {/* Feb/2026: barra bulk para el toggle "código de proveedor". Solo
            aparece si hay al menos 1 conflicto de código en toda la
            importación. Permite actualizar/mantener TODOS de una. */}
        {(() => {
          const indicesConflicto = rows
            .map((r, i) => ({ r, i }))
            .filter(({ r }) => r?.cambia_codigo && r?.existe)
            .map(({ i }) => i);
          if (indicesConflicto.length === 0) return null;
          const total = indicesConflicto.length;
          const nActualizar = indicesConflicto.filter((i) => rows[i]?.aplicar_cambio_codigo).length;
          const nMantener = total - nActualizar;
          return (
            <div className="flex items-center gap-2 flex-wrap text-xs bg-amber-50 rounded-md px-3 py-2 border border-amber-300">
              <span className="text-amber-800 font-semibold flex items-center gap-1.5">
                🏷️ Códigos reales con conflicto: <b>{total}</b>
              </span>
              <span className="text-[10px] text-amber-700">
                ({nActualizar} actualizar · {nMantener} mantener)
              </span>
              <div className="ml-auto flex gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px] border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => aplicarCambioCodigoFamilia(indicesConflicto, true)}
                  data-testid="bulk-actualizar-codigos"
                >
                  Actualizar todos al nuevo
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px] border-slate-400 text-slate-700 hover:bg-slate-50"
                  onClick={() => aplicarCambioCodigoFamilia(indicesConflicto, false)}
                  data-testid="bulk-mantener-codigos"
                >
                  Mantener todos los actuales
                </Button>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Barra flotante de selección múltiple */}
      {seleccion.size > 0 && (
        <div className="p-2.5 bg-primary text-primary-foreground rounded-lg flex items-center gap-2 flex-wrap sticky top-0 z-20 shadow-lg" data-testid="seleccion-barra">
          <span className="text-sm font-semibold flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" /> {seleccion.size} ítem(s) seleccionados
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => setMoverAbiertoPara("seleccion")} data-testid="mover-seleccion-btn">
              <Move className="w-3.5 h-3.5 mr-1" /> Mover a grupo…
            </Button>
            <Button size="sm" variant="ghost" onClick={limpiarSeleccion} className="text-primary-foreground hover:bg-primary-foreground/10">
              Limpiar
            </Button>
          </div>
        </div>
      )}

      {/* Modal "Mover a..." */}
      {moverAbiertoPara && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => { setMoverAbiertoPara(null); setMoverBusqueda(""); }}>
          <div className="bg-card rounded-lg shadow-xl max-w-lg w-full p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="mover-modal">
            <div className="font-semibold text-base">Mover ítems a otra familia</div>
            <div className="text-xs text-muted-foreground">
              {moverAbiertoPara === "seleccion" ? `${seleccion.size} ítem(s) seleccionados` : "Ítems del grupo"}
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold text-muted-foreground mb-1">Elige una familia existente en este contenedor:</div>
              {/* Buscador dentro del modal para filtrar la lista de destinos */}
              <div className="relative mb-2">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                <Input
                  value={moverBusqueda}
                  onChange={(e) => setMoverBusqueda(e.target.value)}
                  placeholder="Buscar grupo por nombre..."
                  className="h-8 text-xs pl-8"
                  autoFocus
                  data-testid="mover-buscar-input"
                />
              </div>
              <div className="max-h-48 overflow-y-auto border border-border rounded-md">
                {(() => {
                  const t = moverBusqueda.trim().toLowerCase();
                  const disponibles = familias.filter((f) => f.key !== moverAbiertoPara);
                  const filtradas = t
                    ? disponibles.filter((f) => {
                        const nom = (f.rows[0]?.nombre_familia_override || (f.rows[0]?.nombre || "").split(" - ")[0] || f.key).toLowerCase();
                        return nom.includes(t) || f.key.toLowerCase().includes(t);
                      })
                    : disponibles;
                  if (filtradas.length === 0) {
                    return (
                      <div className="text-xs text-muted-foreground text-center py-4 px-2">
                        {t ? `Ningún grupo coincide con "${moverBusqueda}"` : "No hay otros grupos disponibles"}
                      </div>
                    );
                  }
                  return filtradas.map((f) => {
                    const nom = f.rows[0]?.nombre_familia_override || (f.rows[0]?.nombre || "").split(" - ")[0] || f.key;
                    return (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => {
                          const idxs = moverAbiertoPara === "seleccion" ? Array.from(seleccion) : (familias.find((x) => x.key === moverAbiertoPara)?.indices || []);
                          moverItems(idxs, f.key, nom);
                          setMoverBusqueda("");
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-primary/10 text-xs border-b border-border/40 last:border-0 flex items-center gap-2"
                        data-testid={`mover-target-${f.key.substring(0, 20)}`}
                      >
                        <Layers className="w-3.5 h-3.5 text-primary" />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{nom}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{f.rows.length} variantes</div>
                        </div>
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
            <div className="pt-1 border-t border-border">
              <div className="text-[10px] uppercase font-bold text-muted-foreground mb-1">…o crea una familia nueva:</div>
              <div className="flex gap-2">
                <Input
                  value={nuevoNombreFamilia}
                  onChange={(e) => setNuevoNombreFamilia(e.target.value)}
                  placeholder="Ej: PAPEL COREANO M12"
                  className="h-9 text-sm flex-1"
                  data-testid="mover-nueva-input"
                />
                <Button
                  size="sm"
                  disabled={!nuevoNombreFamilia.trim()}
                  onClick={() => {
                    const idxs = moverAbiertoPara === "seleccion" ? Array.from(seleccion) : (familias.find((x) => x.key === moverAbiertoPara)?.indices || []);
                    const nueva = nuevoNombreFamilia.trim().toUpperCase();
                    moverItems(idxs, nueva, nuevoNombreFamilia.trim());
                    setMoverBusqueda("");
                  }}
                  data-testid="mover-nueva-btn"
                >
                  Crear
                </Button>
              </div>
            </div>
            <div className="flex justify-end pt-2 border-t border-border">
              <Button size="sm" variant="ghost" onClick={() => { setMoverAbiertoPara(null); setNuevoNombreFamilia(""); setMoverBusqueda(""); }}>Cancelar</Button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de familias */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {familiasFiltradas.map((fam) => {
          const primero = fam.rows[0];
          const familiaMatchExiste = !!primero.familia_existente;
          const fusionActiva = !!primero.familia_key_final;
          const nombreVisible = primero.nombre_familia_override || (primero.nombre || "").split(" - ")[0] || fam.key;
          const stockTotal = fam.rows.reduce((s, r) => s + (r.cantidad || 0), 0);
          const isExpanded = expandida[fam.key] !== false;
          // Feb/2026: si hay búsqueda activa y esta familia tiene rows que matchean,
          // forzar expansión aunque el usuario la haya colapsado antes.
          const tieneMatch = filtroInfo.terminos.length > 0 && fam.indices.some((i) => indicesFiltradosSet.has(i));
          const mostrarExpandido = isExpanded || tieneMatch;
          const fotoActual = primero.foto_fuente || (primero.imagen_key_preview ? "excel" : "sin_foto");
          const buscadorTxt = buscadorAbierto[fam.key] ?? "";
          const sugs = sugerencias[fam.key] || [];
          const seleccionadosEnFam = fam.indices.filter((i) => seleccion.has(i)).length;
          const todosSeleccionadosEnFam = fam.indices.length > 0 && seleccionadosEnFam === fam.indices.length;
          const fuenteMatch = primero.familia_existente?.fuente;
          const confianza = primero.familia_existente?.confianza || 0;
          return (
            <div
              key={fam.key}
              ref={(el) => { if (el) familiaRefs.current[fam.key] = el; }}
              className={`border rounded-lg overflow-hidden transition-all duration-300 ${fusionActiva ? "border-emerald-400 bg-emerald-50/40" : familiaMatchExiste ? "border-amber-300 bg-amber-50/40" : "border-border bg-card"} ${highlightKey === fam.key ? "ring-4 ring-primary ring-offset-2 ring-offset-background shadow-lg scale-[1.01]" : ""}`}
              data-testid={`familia-${fam.key.substring(0, 24)}`}
              onDragOver={onDragOverFamilia}
              onDrop={onDropFamilia(fam.key)}
            >
              {/* Header familia */}
              <div className="p-3 border-b border-border/60 flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={todosSeleccionadosEnFam}
                  onChange={() => toggleSeleccionFamilia(fam, todosSeleccionadosEnFam)}
                  className="w-4 h-4 mt-1 flex-shrink-0"
                  title="Seleccionar toda la familia"
                  data-testid={`fam-check-${fam.key.substring(0, 20)}`}
                />
                <button type="button" onClick={() => setExpandida({ ...expandida, [fam.key]: !isExpanded })} className="flex items-center gap-1 flex-shrink-0 mt-0.5" data-testid={`toggle-fam-${fam.key.substring(0, 20)}`}>
                  <Layers className={`w-4 h-4 ${fusionActiva ? "text-emerald-600" : "text-primary"}`} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Input
                      value={nombreVisible}
                      onChange={(e) => renombrar(fam.indices, e.target.value)}
                      className="h-8 text-sm font-semibold flex-1 max-w-xl min-w-[180px]"
                      data-testid={`fam-nombre-${fam.key.substring(0, 20)}`}
                    />
                    {/* Feb/2026: cuando hay fusión activa, mostramos NUEVAS + EXISTENTES
                        para que quede claro que los colores actuales de la familia NO se pierden. */}
                    {fusionActiva && primero.familia_existente?.variantes_count ? (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-600 text-white uppercase font-semibold flex items-center gap-0.5 flex-shrink-0"
                        title={`${fam.rows.length} variantes nuevas del Excel + ${primero.familia_existente.variantes_count} existentes en la familia`}
                      >
                        <Layers className="w-2.5 h-2.5" />
                        {fam.rows.length} nuevas + {primero.familia_existente.variantes_count} existentes
                      </span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground uppercase font-semibold flex items-center gap-0.5 flex-shrink-0">
                        <Layers className="w-2.5 h-2.5" /> {fam.rows.length} variantes
                      </span>
                    )}
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground uppercase font-semibold">
                      {stockTotal} uds
                    </span>
                    {/* Feb/2026: badge con la referencia del proveedor (ej "EF26-172")
                        para que sea visible en la UI y el usuario pueda confirmar
                        el match cuando busca por esa referencia. */}
                    {primero.familia_ref_excel && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-300 font-mono font-semibold flex items-center gap-0.5"
                        title="Referencia del proveedor (CLIENT'S REF del Excel)"
                      >
                        REF: {primero.familia_ref_excel}
                      </span>
                    )}
                    {fuenteMatch && (
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full uppercase font-semibold flex items-center gap-0.5 ${
                          fuenteMatch === "diccionario" ? "bg-purple-100 text-purple-800 border border-purple-300" :
                          fuenteMatch === "exact" ? "bg-emerald-100 text-emerald-800 border border-emerald-300" :
                          "bg-amber-100 text-amber-800 border border-amber-300"
                        }`}
                        title={fuenteMatch === "diccionario" ? "Match desde el diccionario de referencias guardado" : `Confianza ${confianza}%`}
                      >
                        {fuenteMatch === "diccionario" ? (<><BookOpen className="w-2.5 h-2.5" /> Diccionario</>) :
                         fuenteMatch === "exact" ? (<><Sparkles className="w-2.5 h-2.5" /> Match exacto</>) :
                         (<>≈ {confianza}%</>)}
                      </span>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setMoverAbiertoPara(fam.key)} className="text-xs h-7 px-2" data-testid={`mover-fam-${fam.key.substring(0, 20)}`}>
                      <Move className="w-3 h-3 mr-1" /> Mover grupo
                    </Button>
                  </div>
                  {fusionActiva && (
                    <div className="mt-1 text-[11px] text-emerald-700 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3 h-3" /> Fusionar con familia <b>{primero.familia_key_final}</b>
                      <button type="button" onClick={() => limpiarFusion(fam.indices)} className="ml-1 underline text-rose-600 hover:text-rose-700" data-testid={`limpiar-fusion-${fam.key.substring(0, 20)}`}>
                        (deshacer)
                      </button>
                    </div>
                  )}
                  {familiaMatchExiste && !fusionActiva && (
                    <div className="mt-1 text-[11px] text-amber-800 flex items-center gap-1.5">
                      ⚠️ Sugerencia: ya existe familia <b>"{primero.familia_existente.familia_key}"</b> con {primero.familia_existente.variantes_count} colores. ¿Fusionar?
                      <button type="button" onClick={() => aplicarFusion(fam.indices, {
                        familia_key: primero.familia_existente.familia_key,
                        nombre_representativo: primero.familia_existente.nombre_representativo || (primero.familia_existente.familia_key || "").split(" - ")[0],
                        foto_producto_id: primero.familia_existente.foto_producto_id || null,
                        precio_sugerido_representativo: primero.familia_existente.precio_sugerido_representativo,
                        precio_minimo_representativo: primero.familia_existente.precio_minimo_representativo,
                        costo_representativo: primero.familia_existente.costo_representativo,
                      })} className="ml-1 underline text-emerald-700 hover:text-emerald-800 font-semibold" data-testid={`aplicar-sug-${fam.key.substring(0, 20)}`}>
                        Sí, fusionar
                      </button>
                    </div>
                  )}
                  {/* Feb/2026: Banner de conflicto de código de proveedor.
                      Aparece cuando alguna variante existente ya tiene código Y
                      el Excel trae uno distinto. El toggle decide si se
                      sobrescribe el "código vitrina" del producto. El código
                      del LOTE siempre guarda el del Excel, sin importar aquí. */}
                  {(() => {
                    const rowsConflicto = fam.indices
                      .map((i) => rows[i])
                      .filter((r) => r && r.cambia_codigo && r.existe);
                    if (rowsConflicto.length === 0) return null;
                    // Códigos únicos actuales y del Excel
                    const codigosActuales = [...new Set(rowsConflicto
                      .map((r) => r.actual?.codigo)
                      .filter(Boolean))];
                    const codigosNuevos = [...new Set(rowsConflicto
                      .map((r) => r.familia_ref_excel)
                      .filter(Boolean))];
                    // ¿El toggle está activo para TODAS las variantes en conflicto?
                    const todasActualizar = rowsConflicto.every((r) => r.aplicar_cambio_codigo);
                    const todasMantener = rowsConflicto.every((r) => !r.aplicar_cambio_codigo);
                    return (
                      <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[12px]">
                        <div className="flex items-start gap-2">
                          <span className="text-amber-700 mt-0.5">🏷️</span>
                          <div className="flex-1">
                            <div className="font-semibold text-amber-900 mb-1">
                              Cambio de código detectado ({rowsConflicto.length} {rowsConflicto.length === 1 ? "variante" : "variantes"})
                            </div>
                            <div className="text-[11px] text-amber-800 mb-2 flex flex-wrap gap-x-3 gap-y-1">
                              <span>
                                Actual en catálogo:{" "}
                                <span className="font-mono font-semibold">
                                  {codigosActuales.join(" · ") || "—"}
                                </span>
                              </span>
                              <span>
                                Nuevo lote (Excel):{" "}
                                <span className="font-mono font-semibold text-emerald-700">
                                  {codigosNuevos.join(" · ") || "—"}
                                </span>
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              <button
                                type="button"
                                onClick={() => aplicarCambioCodigoFamilia(fam.indices, true)}
                                className={`text-[11px] px-2 py-1 rounded border font-semibold ${
                                  todasActualizar
                                    ? "bg-emerald-600 text-white border-emerald-700"
                                    : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                }`}
                                data-testid={`fam-actualizar-codigo-${fam.key.substring(0, 20)}`}
                              >
                                {todasActualizar ? "✓ " : ""}Actualizar al nuevo código
                              </button>
                              <button
                                type="button"
                                onClick={() => aplicarCambioCodigoFamilia(fam.indices, false)}
                                className={`text-[11px] px-2 py-1 rounded border font-semibold ${
                                  todasMantener
                                    ? "bg-slate-700 text-white border-slate-800"
                                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                                }`}
                                data-testid={`fam-mantener-codigo-${fam.key.substring(0, 20)}`}
                              >
                                {todasMantener ? "✓ " : ""}Mantener el actual
                              </button>
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1.5 italic">
                              💡 El nuevo lote SIEMPRE guarda su propio código en el histórico. Esta decisión solo afecta cuál se muestra como código "vitrina" en el catálogo y product picker.
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Panel de acciones */}
              <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3 bg-background/60">
                {/* Buscar/fusionar familia existente */}
                <div>
                  <div className="text-[10px] uppercase font-bold text-muted-foreground mb-1">
                    Fusionar con familia existente
                    <span className="ml-1 text-muted-foreground/60 normal-case font-normal" title="Al fusionar, los colores nuevos se agregarán al catálogo y los colores repetidos sumarán stock.">
                      · colores nuevos se agregan, repetidos suman stock
                    </span>
                  </div>
                  <div className="relative">
                    <Input
                      value={buscadorTxt}
                      onChange={(e) => buscarFamilia(fam.key, e.target.value)}
                      placeholder="Buscar por nombre o referencia…"
                      className="h-9 text-sm"
                      data-testid={`fam-buscar-${fam.key.substring(0, 20)}`}
                    />
                    {sugs.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg max-h-72 overflow-y-auto z-10">
                        {sugs.map((s) => (
                          <button
                            key={s.familia_key}
                            type="button"
                            onClick={() => { aplicarFusion(fam.indices, s); setBuscadorAbierto({ ...buscadorAbierto, [fam.key]: "" }); setSugerencias({ ...sugerencias, [fam.key]: [] }); }}
                            className="w-full text-left px-3 py-2 hover:bg-primary/10 text-xs border-b border-border/40 last:border-0 flex items-center gap-2"
                            data-testid={`fam-sug-${s.familia_key.substring(0, 20)}`}
                          >
                            {s.foto_producto_id ? (
                              <img src={`${API}/api/catalogo/img/${s.foto_producto_id}?w=80`} alt="" className="w-10 h-10 object-cover rounded flex-shrink-0 border border-border" />
                            ) : (
                              <div className="w-10 h-10 rounded border border-dashed border-border/60 flex items-center justify-center flex-shrink-0 bg-muted/40">
                                <ImageIcon className="w-3.5 h-3.5 text-muted-foreground/50" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold truncate flex items-center gap-1.5">
                                {s.nombre_representativo}
                                <span className="text-[9px] px-1 py-0.5 rounded-full bg-primary/15 text-primary font-semibold">
                                  {s.variantes_count} {s.variantes_count === 1 ? "color" : "colores"}
                                </span>
                              </div>
                              <div className="text-[10px] font-mono text-muted-foreground/80 truncate">
                                {s.familia_key}
                              </div>
                              {s.colores_ya_registrados.length > 0 && (
                                <div className="text-[10px] text-muted-foreground truncate mt-0.5">
                                  Colores: {s.colores_ya_registrados.slice(0, 5).join(" · ")}
                                  {s.colores_ya_registrados.length > 5 ? ` +${s.colores_ya_registrados.length - 5}` : ""}
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {buscadorTxt.length >= 1 && sugs.length === 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg z-10 p-3 text-xs text-muted-foreground text-center">
                        No hay familias existentes con "{buscadorTxt}". Se creará una familia nueva.
                      </div>
                    )}
                  </div>
                </div>

                {/* Foto: elegir fuente */}
                <div>
                  <div className="text-[10px] uppercase font-bold text-muted-foreground mb-1 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Fuente de foto</div>
                  <select
                    value={fotoActual}
                    onChange={(e) => setFotoFuente(fam.indices, e.target.value)}
                    className="w-full h-9 border border-border rounded-md px-2 text-sm bg-card"
                    data-testid={`fam-foto-${fam.key.substring(0, 20)}`}
                  >
                    <option value="excel">📎 Del Excel del contenedor {primero.imagen_key_preview ? "" : "(no hay foto)"}</option>
                    {fusionActiva && <option value="heredar">🎨 Heredar de familia match</option>}
                    <option value="sin_foto">🚫 Sin foto</option>
                  </select>
                </div>

                {/* Editar precios (aplica a todas las variantes) */}
                <div>
                  <div className="text-[10px] uppercase font-bold text-muted-foreground mb-1 flex items-center gap-1"><Edit2 className="w-3 h-3" /> Precios (para toda la familia)</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-muted-foreground">Precio sug.</label>
                      <Input
                        type="number"
                        value={primero.precio_sugerido || 0}
                        onChange={(e) => cambiarPrecios(fam.indices, "precio_sugerido", e.target.value)}
                        className="h-8 text-xs"
                        data-testid={`fam-precio-sug-${fam.key.substring(0, 20)}`}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground">Precio mín.</label>
                      <Input
                        type="number"
                        value={primero.precio_minimo || 0}
                        onChange={(e) => cambiarPrecios(fam.indices, "precio_minimo", e.target.value)}
                        className="h-8 text-xs"
                        data-testid={`fam-precio-min-${fam.key.substring(0, 20)}`}
                      />
                    </div>
                  </div>
                  {/* Feb/2026: si el usuario acaba de fusionar contra una familia existente,
                      le mostramos DOS opciones claras (mantener actuales / usar nuevos) con
                      indicador visual del modo activo. Al elegir "nuevos" mostramos aviso
                      de que se propaga a la familia existente. */}
                  {fusionActiva && primero.familia_precios_ref && (
                    <div className="mt-2 p-2 rounded-md bg-muted/40 border border-border" data-testid={`fam-precios-toggle-${fam.key.substring(0, 20)}`}>
                      <div className="text-[10px] uppercase font-bold text-muted-foreground mb-1.5">
                        ¿Qué precios usar en esta familia?
                      </div>
                      <div className="grid grid-cols-1 gap-1.5">
                        {/* Opción 1: precios actuales */}
                        <button
                          type="button"
                          onClick={() => usarPreciosFamiliaExistente(fam.indices, primero.familia_precios_ref)}
                          className={`text-left p-2 rounded-md border transition-all ${
                            primero.familia_precios_modo === "existente"
                              ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40 ring-1 ring-emerald-400"
                              : "border-border bg-card hover:border-emerald-300"
                          }`}
                          data-testid={`fam-opcion-existente-${fam.key.substring(0, 20)}`}
                        >
                          <div className="flex items-center gap-2 mb-0.5">
                            <div className={`w-3 h-3 rounded-full flex-shrink-0 border-2 ${
                              primero.familia_precios_modo === "existente"
                                ? "border-emerald-500 bg-emerald-500"
                                : "border-muted-foreground/40"
                            }`} />
                            <div className="text-[11px] font-bold text-foreground flex-1">Mantener precios ACTUALES de la familia</div>
                            {primero.familia_precios_modo === "existente" && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white font-bold uppercase">Activo</span>
                            )}
                          </div>
                          <div className="text-[10px] text-muted-foreground tabular-nums pl-5">
                            Sug {formatCOP(primero.familia_precios_ref.precio_sugerido || 0)} · Mín {formatCOP(primero.familia_precios_ref.precio_minimo || 0)}
                          </div>
                          <div className="text-[9px] text-muted-foreground/80 pl-5 mt-0.5">
                            El contenedor solo suma stock. Los precios existentes no se tocan.
                          </div>
                        </button>

                        {/* Opción 2: precios nuevos del Excel */}
                        <button
                          type="button"
                          onClick={() => {
                            // Feb/2026: al elegir precios nuevos, avisamos que se propaga a la familia existente.
                            const originalSug = primero.precio_sugerido_excel_original || primero.precio_sugerido || 0;
                            const originalMin = primero.precio_minimo_excel_original || primero.precio_minimo || 0;
                            const variantes = primero.familia_existente?.variantes_count || "todas";
                            const confirmMsg =
                              `¿Aplicar precios NUEVOS a TODA la familia?\n\n` +
                              `• Nuevo sugerido: ${formatCOP(originalSug)}\n` +
                              `• Nuevo mínimo: ${formatCOP(originalMin)}\n\n` +
                              `⚠ IMPORTANTE: esto cambia los precios de las ${variantes} variantes que ya existen en la familia "${primero.nombre_familia_override || fam.key}", no solo las que llegan en este contenedor.\n\n` +
                              `Aceptar → aplicar el cambio a todas.\n` +
                              `Cancelar → no cambiar nada.`;
                            if (window.confirm(confirmMsg)) {
                              usarPreciosNuevos(fam.indices);
                            }
                          }}
                          className={`text-left p-2 rounded-md border transition-all ${
                            primero.familia_precios_modo === "excel"
                              ? "border-primary bg-primary/10 ring-1 ring-primary/60"
                              : "border-border bg-card hover:border-primary/40"
                          }`}
                          data-testid={`fam-opcion-excel-${fam.key.substring(0, 20)}`}
                        >
                          <div className="flex items-center gap-2 mb-0.5">
                            <div className={`w-3 h-3 rounded-full flex-shrink-0 border-2 ${
                              primero.familia_precios_modo === "excel"
                                ? "border-primary bg-primary"
                                : "border-muted-foreground/40"
                            }`} />
                            <div className="text-[11px] font-bold text-foreground flex-1">Usar precios NUEVOS del Excel (para toda la familia)</div>
                            {primero.familia_precios_modo === "excel" && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground font-bold uppercase">Activo</span>
                            )}
                          </div>
                          <div className="text-[10px] text-muted-foreground tabular-nums pl-5">
                            Sug {formatCOP(primero.precio_sugerido_excel_original || primero.precio_sugerido || 0)} · Mín {formatCOP(primero.precio_minimo_excel_original || primero.precio_minimo || 0)}
                          </div>
                          <div className="text-[9px] text-primary/80 pl-5 mt-0.5 font-medium">
                            ⚠ Propaga el nuevo precio a todas las variantes existentes de la familia.
                          </div>
                        </button>
                      </div>
                      {primero.familia_precios_modo === null && (
                        <div className="text-[10px] text-amber-700 dark:text-amber-300 mt-1.5 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                          Aún no eliges — por defecto sigue el flujo del paso 2 (checkbox "Aplicar cambio de precio" por fila).
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Lista de variantes (expandible) con miniaturas + checkboxes + drag */}
              {mostrarExpandido && (
                <div className="border-t border-border/40 divide-y divide-border/40 text-xs">
                  {/* Feb/2026: cuando el usuario busca algo, filtramos las filas
                      visibles a solo las que matchean (más limpio). Excepción:
                      si la búsqueda matcheó por familia_key (todas las filas
                      forman parte del match implícito) → mostramos todas.
                      El toggle de abajo permite ver todas si quiere. */}
                  {(() => {
                    let filasAMostrar = fam.rows;
                    const hayBusqueda = filtroInfo.terminos.length > 0;
                    const familiaKeyMatch = hayBusqueda && filtroInfo.terminos.some((t) => fam.key.toLowerCase().includes(t));
                    const verTodas = !!verTodasEnFam[fam.key];
                    if (hayBusqueda && !familiaKeyMatch && !verTodas) {
                      const filas = fam.rows.filter((r) => indicesFiltradosSet.has(r.__idx));
                      if (filas.length > 0 && filas.length < fam.rows.length) {
                        filasAMostrar = filas;
                      }
                    }
                    return (
                      <>
                        {hayBusqueda && filasAMostrar.length < fam.rows.length && (
                          <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-200 text-[10px] text-amber-800 flex items-center justify-between">
                            <span>Mostrando <b>{filasAMostrar.length}</b> de <b>{fam.rows.length}</b> variantes que coinciden con la búsqueda</span>
                            <button
                              type="button"
                              onClick={() => setVerTodasEnFam({ ...verTodasEnFam, [fam.key]: true })}
                              className="text-amber-900 underline hover:text-amber-950 font-semibold"
                            >
                              Ver todas
                            </button>
                          </div>
                        )}
                        {hayBusqueda && verTodas && filasAMostrar.length > 0 && (
                          <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] text-slate-700 flex items-center justify-between">
                            <span>Mostrando todas las <b>{fam.rows.length}</b> variantes</span>
                            <button
                              type="button"
                              onClick={() => setVerTodasEnFam({ ...verTodasEnFam, [fam.key]: false })}
                              className="text-slate-800 underline hover:text-slate-950 font-semibold"
                            >
                              Solo coincidencias
                            </button>
                          </div>
                        )}
                        {filasAMostrar.map((r) => {
                    const seleccionado = seleccion.has(r.__idx);
                    const esMatch = indicesFiltradosSet.has(r.__idx);
                    // Preferir el thumb base64 embebido en la respuesta del preview
                    // (evita race con background tasks). Fallback al endpoint clásico si no vino.
                    const thumbUrl = r.imagen_thumb_b64
                      ? r.imagen_thumb_b64
                      : (r.imagen_key_preview ? `${API}/api/productos/_/borrador-imagen?key=${encodeURIComponent(r.imagen_key_preview)}` : null);
                    return (
                      <div
                        key={r.sku}
                        className={`px-3 py-2 flex items-center gap-2 ${seleccionado ? "bg-primary/10" : ""} ${esMatch && !seleccionado ? "bg-amber-50 ring-1 ring-amber-300" : ""}`}
                        draggable
                        onDragStart={onDragStartItem(r.__idx, r.sku)}
                        data-testid={`item-${r.sku}`}
                        title={esMatch ? "Coincide con la búsqueda actual — arrastra para mover a otra familia" : "Arrastra para mover a otra familia"}
                      >
                        <input
                          type="checkbox"
                          checked={seleccionado}
                          onChange={() => toggleSeleccion(r.__idx)}
                          className="w-4 h-4 flex-shrink-0"
                          data-testid={`item-check-${r.sku}`}
                        />
                        {thumbUrl ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const idxInFam = fam.rows.findIndex((x) => x.__idx === r.__idx);
                              setLightbox({ familiaKey: fam.key, index: idxInFam >= 0 ? idxInFam : 0 });
                            }}
                            className="w-10 h-10 rounded border border-border flex-shrink-0 bg-muted overflow-hidden relative group"
                            title="Click para ver más grande"
                            data-testid={`item-thumb-${r.sku}`}
                          >
                            <img
                              src={thumbUrl}
                              alt=""
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                            <div className="absolute inset-0 bg-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <ZoomIn className="w-3.5 h-3.5 text-white" />
                            </div>
                          </button>
                        ) : (
                          <div className="w-10 h-10 rounded border border-dashed border-border flex items-center justify-center flex-shrink-0 bg-muted/40">
                            <ImageIcon className="w-4 h-4 text-muted-foreground/50" />
                          </div>
                        )}
                        <span className="font-mono text-[10px] text-muted-foreground w-20 truncate">{r.sku}</span>
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{r.color ? <b className="text-foreground">{r.color}</b> : <em className="text-muted-foreground/60">sin color</em>} · {r.nombre}</div>
                        </div>
                        <span className="text-muted-foreground tabular-nums">{r.cantidad} uds</span>
                        <span className="text-foreground/70 tabular-nums w-24 text-right">{formatCOP(r.precio_sugerido || 0)}</span>
                        <button
                          type="button"
                          onClick={() => {
                            // Mover un solo ítem: abrir el modal con este ítem preseleccionado
                            setSeleccion(new Set([r.__idx]));
                            setMoverAbiertoPara("seleccion");
                          }}
                          className="p-1 text-muted-foreground hover:text-primary"
                          title="Mover a otra familia"
                          data-testid={`mover-item-${r.sku}`}
                        >
                          <Move className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
        {familiasFiltradas.length === 0 && (
          <div className="text-center text-sm text-muted-foreground py-10 space-y-3">
            <div>
              {busca || soloSinDecidir
                ? "No hay familias que coincidan con los filtros activos."
                : "No hay familias para mostrar."}
            </div>
            {(busca || soloSinDecidir) && familias.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setBusca(""); setSoloSinDecidir(false); }}
                data-testid="paso3-limpiar-filtros"
              >
                Limpiar filtros y ver los {familias.length} grupos
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Footer navegación */}
      <div className="pt-3 border-t border-border flex items-center justify-between gap-2">
        <Button type="button" variant="outline" onClick={onVolver} disabled={loading} data-testid="paso3-volver">
          Volver a precios
        </Button>
        <div className="text-[11px] text-muted-foreground max-w-md text-center">
          {loading ? (
            <span className="text-primary font-semibold animate-pulse">⏳ Procesando contenedor… no cierres esta ventana ni oprimas de nuevo.</span>
          ) : (
            <>💡 Tip: los mapeos que aceptes se guardan como diccionario y se pre-aplicarán en futuras importaciones del mismo proveedor.</>
          )}
        </div>
        <Button
          type="button"
          onClick={onContinuar}
          disabled={loading}
          className="bg-primary hover:bg-primary/90 min-w-[180px]"
          data-testid="paso3-continuar"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Procesando…
            </>
          ) : (
            "Continuar a confirmar"
          )}
        </Button>
      </div>

      {/* Lightbox para ver thumbs del Excel en grande, con navegación entre items de la misma familia */}
      {(() => {
        if (!lightbox) return null;
        // Filtrar solo items de esa familia que tengan thumb (navegación útil)
        const fam = familias.find((f) => f.key === lightbox.familiaKey);
        const conFoto = fam ? fam.rows.filter((r) => r.imagen_thumb_b64) : [];
        if (conFoto.length === 0) return null;
        const idx = Math.max(0, Math.min(lightbox.index, conFoto.length - 1));
        const item = conFoto[idx];
        const alt = `${item.color || "sin color"} · ${item.sku} · ${item.nombre}`;
        const navigate = (delta) => {
          setLightbox({ ...lightbox, index: (idx + delta + conFoto.length) % conFoto.length });
        };
        return (
          <ImageLightbox
            src={item.imagen_thumb_b64}
            alt={alt}
            open={true}
            onOpenChange={(v) => !v && setLightbox(null)}
            onPrev={conFoto.length > 1 ? () => navigate(-1) : null}
            onNext={conFoto.length > 1 ? () => navigate(1) : null}
            position={idx + 1}
            total={conFoto.length}
          />
        );
      })()}
    </div>
  );
}
